'use client'

import { useEffect, useRef } from 'react'
import { FederalDistrictCheckboxList, useFederalDistricts, type FederalDistrictItem } from '@entities/federal-district'
import { Alert, AlertDescription, AlertTitle } from '@ui/base/alert'
import { Button } from '@ui/base/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ui/base/card'
import { Checkbox } from '@ui/base/checkbox'
import { Field, FieldError, FieldLabel, FieldPanel, FieldTitle } from '@ui/base/field'
import { Input } from '@ui/base/input'
import { RadioGroup, RadioGroupItem } from '@ui/base/radio-group'
import { Separator } from '@ui/base/separator'
import { useFieldArray, useFormContext } from 'react-hook-form'

import { type FulfillmentFormValues, type MarketplaceForm } from '../model/types'
import { RequiredMark } from './required-mark'

const builtInMarketplaces = [
  ['wildberries', 'Wildberries'],
  ['ozon', 'Ozon'],
  ['yandex_market', 'Я.Маркет'],
  ['magnit_market', 'Магнит Маркет'],
] as const

const emptyMarketplace = (marketplace: MarketplaceForm['marketplace']): MarketplaceForm => ({
  marketplace,
  otherName: '',
  coverageMode: marketplace === 'wildberries' ? 'selected' : 'all',
  destinations: [],
})

const marketplaceDisplayName = (item: MarketplaceForm, customNumber: number) => {
  if (item.marketplace === 'other') {
    return item.otherName.trim() || `Другой маркетплейс ${customNumber}`
  }
  return builtInMarketplaces.find(([value]) => value === item.marketplace)?.[1] ?? item.marketplace
}

export const MarketplaceSection = () => {
  const {
    control,
    register,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = useFormContext<FulfillmentFormValues>()
  const {
    districts,
    isLoading: isDistrictsLoading,
    hasLoadError: districtRequestFailed,
    retry: retryDistricts,
  } = useFederalDistricts()
  const { fields, append, remove } = useFieldArray({ control, name: 'marketplaces' })
  const marketplaces = watch('marketplaces') ?? []
  const otherItems = fields.flatMap((field, index) => {
    const item = marketplaces[index]
    if (!item || item.marketplace !== 'other') {
      return []
    }
    return [
      {
        field,
        item,
        index,
        customNumber: marketplaces.slice(0, index + 1).filter((marketplace) => marketplace.marketplace === 'other')
          .length,
      },
    ]
  })
  const firstOther = otherItems[0]
  const additionalOtherItems = otherItems.slice(1)
  const marketplacesError = errors.marketplaces?.root?.message ?? (errors.marketplaces?.message as string | undefined)
  const marketplacesErrorId = 'fulfillment-marketplaces-error'

  /**
   * Требование «выберите хотя бы один маркетплейс» относится ко всему списку, поэтому подпись о
   * нём снимает только проверка всего списка. Перепроверяем после того, как список изменился, и
   * лишь когда подпись уже висит на экране: иначе форма ругалась бы на пустой список сразу при
   * открытии шага. Проверка идёт отдельным шагом, а не в самом обработчике: добавление и удаление
   * площадки применяются к списку не мгновенно.
   */
  const isListInvalid = useRef(false)
  isListInvalid.current = Boolean(errors.marketplaces)
  useEffect(() => {
    if (isListInvalid.current) {
      void trigger('marketplaces')
    }
  }, [marketplaces.length, trigger])

  const toggleBuiltIn = (marketplace: MarketplaceForm['marketplace'], checked: boolean) => {
    const index = marketplaces.findIndex((item) => item.marketplace === marketplace)
    if (checked && index === -1) {
      append(emptyMarketplace(marketplace))
    }
    if (!checked && index >= 0) {
      remove(index)
    }
  }

  const toggleOther = (checked: boolean) => {
    if (checked && !firstOther) {
      append(emptyMarketplace('other'))
    }
    if (!checked && firstOther) {
      remove(firstOther.index)
    }
  }

  const marketplaceSettings = (item: MarketplaceForm, index: number, customNumber: number, canRemove = false) => {
    const displayName = marketplaceDisplayName(item, customNumber)
    const fieldErrors = errors.marketplaces?.[index]
    const otherNameErrorId = `fulfillment-marketplace-${index}-other-name-error`
    const districtsLabelId = `fulfillment-marketplace-${index}-districts-label`
    const districtsOptionsLabelId = `fulfillment-marketplace-${index}-districts-options-label`
    const destinationsErrorId = `fulfillment-marketplace-${index}-destinations-error`
    const districtRequestErrorId = `fulfillment-marketplace-${index}-district-request-error`
    // Пока перечня округов нет на экране, требование выбрать округ молчит: выполнить его нечем.
    const destinationsError = districts ? fieldErrors?.destinations?.message : undefined
    const hasDistrictsError = districtRequestFailed || Boolean(destinationsError)
    const districtsErrorId = districtRequestFailed ? districtRequestErrorId : destinationsErrorId
    const modeLabels = [
      ['all', `Во все федеральные округа ${displayName}`],
      ['selected', `В выбранные округа ${displayName}`],
    ] as const
    const toggleDistrict = (district: FederalDistrictItem, checked: boolean) => {
      setValue(
        `marketplaces.${index}.destinations`,
        checked
          ? [...item.destinations, district]
          : item.destinations.filter((destination) => destination.code !== district.code),
        { shouldValidate: true, shouldDirty: true }
      )
      if (checked && item.needsDistrictReselection) {
        setValue(`marketplaces.${index}.needsDistrictReselection`, false, { shouldDirty: true })
      }
    }

    return (
      <>
        {item.marketplace === 'other' && (
          <Field data-invalid={Boolean(fieldErrors?.otherName?.message)}>
            <FieldLabel htmlFor={`fulfillment-marketplace-${index}-other-name`}>
              Название другого маркетплейса {customNumber} <RequiredMark />
            </FieldLabel>
            <Input
              id={`fulfillment-marketplace-${index}-other-name`}
              aria-invalid={Boolean(fieldErrors?.otherName?.message)}
              aria-describedby={fieldErrors?.otherName?.message ? otherNameErrorId : undefined}
              {...register(`marketplaces.${index}.otherName`)}
            />
            <FieldError id={otherNameErrorId}>{fieldErrors?.otherName?.message}</FieldError>
          </Field>
        )}

        <RadioGroup
          value={item.coverageMode}
          onValueChange={(coverageMode) => {
            setValue(`marketplaces.${index}.coverageMode`, coverageMode as MarketplaceForm['coverageMode'], {
              shouldValidate: true,
              shouldDirty: true,
            })
            if (coverageMode === 'all' && item.needsDistrictReselection) {
              setValue(`marketplaces.${index}.needsDistrictReselection`, false, { shouldDirty: true })
            }
          }}
          aria-label={`Направления ${displayName}`}
        >
          {modeLabels.map(([value, label]) => {
            const id = `fulfillment-marketplace-${index}-coverage-${value}`
            return (
              <FieldLabel key={value} htmlFor={id}>
                <Field orientation="horizontal">
                  <RadioGroupItem id={id} size="lg" value={value} aria-labelledby={`${id}-title`} />
                  <FieldTitle id={`${id}-title`}>{label}</FieldTitle>
                </Field>
              </FieldLabel>
            )
          })}
        </RadioGroup>

        {item.coverageMode === 'selected' && item.needsDistrictReselection && (
          <Alert variant="warning">
            <AlertTitle>Выберите федеральные округа заново</AlertTitle>
            <AlertDescription>
              Направления теперь задаются федеральными округами. Прежние сохранённые значения показать нельзя. Пока
              округа не выбраны, вам приходят заявки FBS из всех округов — как только выберете конкретные, остальные
              перестанут приходить.
            </AlertDescription>
          </Alert>
        )}

        {item.coverageMode === 'selected' && (
          <>
            {/* Черта отбивает перечень округов от переключателя охвата: без неё длинный список
                читается как продолжение вариантов выбора. */}
            <Separator />
            <Field
              aria-labelledby={districtsLabelId}
              aria-describedby={hasDistrictsError ? districtsErrorId : undefined}
              aria-invalid={hasDistrictsError}
              data-invalid={hasDistrictsError}
            >
              {/* Имя группы берём с видимой подписи, а не задаём отдельной строкой: иначе отметка
                обязательности не попадала бы в озвучку. Название площадки в подписи обязательно:
                площадок на шаге несколько, и без него все наборы округов звучали бы одинаково. */}
              <FieldLabel variant="section" id={districtsLabelId}>
                Федеральные округа {displayName} <RequiredMark />
              </FieldLabel>
              <span id={districtsOptionsLabelId} className="sr-only">
                Варианты округов {displayName}
              </span>
              <FederalDistrictCheckboxList
                districts={districts}
                selectedCodes={item.destinations.map((destination) => destination.code ?? '')}
                onToggle={toggleDistrict}
                isLoading={isDistrictsLoading}
                hasLoadError={districtRequestFailed}
                onRetry={retryDistricts}
                labelId={districtsOptionsLabelId}
                errorDescriptionId={districtRequestErrorId}
              />
              <FieldError id={destinationsErrorId}>{destinationsError}</FieldError>
            </Field>
          </>
        )}

        {canRemove && (
          <Button type="button" variant="destructive" onClick={() => remove(index)}>
            Удалить {displayName}
          </Button>
        )}
      </>
    )
  }

  return (
    <div className="space-y-4">
      <Field
        aria-label="Маркетплейсы для FBS"
        aria-invalid={Boolean(marketplacesError)}
        aria-describedby={marketplacesError ? marketplacesErrorId : undefined}
        data-invalid={Boolean(marketplacesError)}
      >
        {/* Видимой подписи здесь нет: над списком уже стоит заголовок «Площадки и федеральные округа».
            Имя группе даёт aria-label выше — без него список потерял бы имя для озвучки. */}
        <div className="space-y-3">
          {builtInMarketplaces.map(([value, label]) => {
            const id = `fulfillment-marketplace-${value}`
            const index = marketplaces.findIndex((item) => item.marketplace === value)
            const item = index >= 0 ? marketplaces[index] : null
            return (
              <div key={value} className="space-y-3">
                <FieldLabel htmlFor={id}>
                  <Field orientation="horizontal">
                    <Checkbox
                      id={id}
                      size="lg"
                      aria-labelledby={`${id}-title`}
                      checked={item !== null}
                      onCheckedChange={(checked) => toggleBuiltIn(value, checked === true)}
                    />
                    <FieldTitle id={`${id}-title`}>{label}</FieldTitle>
                  </Field>
                </FieldLabel>
                {/* Настройки площадки лежат на белой подложке: серая слилась бы с панелью шага. */}
                {item && <FieldPanel variant="card">{marketplaceSettings(item, index, 0)}</FieldPanel>}
              </div>
            )
          })}
          <div className="space-y-3">
            <FieldLabel htmlFor="fulfillment-marketplace-other">
              <Field orientation="horizontal">
                <Checkbox
                  id="fulfillment-marketplace-other"
                  size="lg"
                  aria-labelledby="fulfillment-marketplace-other-title"
                  checked={Boolean(firstOther)}
                  onCheckedChange={(checked) => toggleOther(checked === true)}
                />
                <FieldTitle id="fulfillment-marketplace-other-title">Другой маркетплейс</FieldTitle>
              </Field>
            </FieldLabel>
            {firstOther && (
              <FieldPanel variant="card">
                {marketplaceSettings(firstOther.item, firstOther.index, firstOther.customNumber)}
              </FieldPanel>
            )}
          </div>
        </div>
        <FieldError id={marketplacesErrorId}>{marketplacesError}</FieldError>
      </Field>

      {additionalOtherItems.map(({ field, item, index, customNumber }) => (
        <Card key={field.id}>
          <CardHeader>
            <CardTitle>{marketplaceDisplayName(item, customNumber)}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">{marketplaceSettings(item, index, customNumber, true)}</CardContent>
        </Card>
      ))}

      {firstOther && (
        <Button type="button" variant="outline" onClick={() => append(emptyMarketplace('other'))}>
          Добавить другой маркетплейс
        </Button>
      )}
    </div>
  )
}
